import React, { useState, useRef } from 'react';

const platformLogos = {
  'Cal.com': '/images/slider/calcom.svg',
};

const platforms = [
  {
    name: 'Cal.com',
    fields: ['Cal.com API Key', 'Cal.com Event Type ID'],
    link: 'https://cal.com/docs/api-reference'
  }
];

const Slider = ({ onDataChange }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [hoveredPlatform, setHoveredPlatform] = useState(null);
  const [fieldValues, setFieldValues] = useState({});

  const getItemsPerView = () => {
    if (typeof window === 'undefined') return 1;

    const width = window.innerWidth;
    if (width >= 1024) return 6;
    if (width >= 768) return 4;
    if (width >= 640) return 3;
    return 1;
  };

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

  React.useEffect(() => {
    const handleResize = () => {
      setItemsPerView(getItemsPerView());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    if (onDataChange) {
      onDataChange(fieldValues);
    }
  }, [fieldValues, onDataChange]);

  const nextSlide = () => {
    setCurrentIndex(prev =>
      Math.min(prev + itemsPerView, platforms.length - itemsPerView)
    );
  };

  const prevSlide = () => {
    setCurrentIndex(prev => Math.max(prev - itemsPerView, 0));
  };

  const handlePlatformClick = (platform) => {
    if (selectedPlatform?.name !== platform.name) {
      setFieldValues({});
    }
    setSelectedPlatform(selectedPlatform?.name === platform.name ? null : platform);
  };

  const handleFieldChange = (platformName, fieldName, value) => {
    setFieldValues(prev => ({
      ...prev,
      [platformName]: {
        ...prev[platformName],
        [fieldName]: value
      }
    }));
  };

  const visiblePlatforms = platforms.slice(currentIndex, currentIndex + itemsPerView);

  const canGoNext = currentIndex < platforms.length - itemsPerView;
  const canGoPrev = currentIndex > 0;

  return (
    <div className="card">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Connect With</h3>
      <p className="text-sm text-gray-600 mb-6">
        Seamlessly sync call data across your favorite integrations — updating your CRM, booking appointments, and triggering custom workflows automatically — so your team can focus on growth.
      </p>

      {platforms.length > 0 && (
        <div className="w-full lg:w-2/5">
          <div className="relative mb-8">
            {platforms.length > itemsPerView && canGoPrev && (
              <button
                onClick={prevSlide}
                className="absolute -left-2 sm:-left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-1 sm:p-2 shadow-md hover:shadow-lg transition-shadow"
                aria-label="Previous platforms"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {platforms.length > itemsPerView && canGoNext && (
              <button
                onClick={nextSlide}
                className="absolute -right-2 sm:-right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-1 sm:p-2 shadow-md hover:shadow-lg transition-shadow"
                aria-label="Next platforms"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            <div className={`
              grid gap-2 sm:gap-3
              ${platforms.length === 1
                ? 'grid-cols-1 max-w-[120px] sm:max-w-[150px]'
                : `grid-cols-1 
                   sm:grid-cols-2 
                   md:grid-cols-3 
                   lg:grid-cols-${Math.min(6, itemsPerView)}`
              }
            `}>
              {visiblePlatforms.map((platform) => (
                <div
                  key={platform.name}
                  className="relative group"
                >
                  <button
                    type="button"
                    onClick={() => handlePlatformClick(platform)}
                    onMouseEnter={() => setHoveredPlatform(platform)}
                    onMouseLeave={() => setHoveredPlatform(null)}
                    className={`
                      w-full aspect-square rounded-lg border-2 transition-all duration-200 
                      flex flex-col items-center justify-center
                      ${platforms.length === 1 ? 'p-1.5' : 'p-2 sm:p-3'} 
                      ${selectedPlatform?.name === platform.name
                        ? 'border-primary-500 bg-primary-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                      }
                    `}
                  >
                    <div className={`
                      flex items-center justify-center w-full mb-1 
                      ${platforms.length === 1 ? 'h-6 sm:h-8' : 'h-8 sm:h-10'} 
                    `}>
                      <img
                        src={platformLogos[platform.name]}
                        alt={platform.name}
                        className={`
                          max-w-full object-contain 
                          ${platforms.length === 1 ? 'max-h-5 sm:max-h-6' : 'max-h-6 sm:max-h-8'} 
                        `}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div
                        className="text-sm font-semibold text-gray-600 text-center hidden"
                        style={{ display: 'none' }}
                      >
                        {platform.name}
                      </div>
                    </div>

                    <div className={`
                      text-gray-500 text-center 
                      ${platforms.length === 1 ? 'text-xs' : 'text-xs sm:text-sm'}
                    `}>
                      {platform.name}
                    </div>

                    <div className={`
                      absolute top-1 right-1
                      ${platforms.length === 1 ? 'top-0.5 right-0.5' : 'top-1 right-1'}  
                      sm:block hidden
                    `}>
                      <a
                        href={platform.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-primary-600 text-white rounded-full p-1 shadow-lg hover:bg-primary-700 transition-colors flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>

                    <div className="sm:hidden absolute top-1 right-1">
                      <a
                        href={platform.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-primary-600 text-white rounded-full p-1 shadow-lg flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-2.5 h-2-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </a>
                    </div>
                  </button>

                  {hoveredPlatform?.name === platform.name && (
                    <div className="hidden sm:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-20">
                      <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                        Integration Guide for {platform.name}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {platforms.length > itemsPerView && (
        <div className="flex justify-center mb-8 space-x-1.5">
          {Array.from({ length: Math.ceil(platforms.length / itemsPerView) }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index * itemsPerView)}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${index === Math.floor(currentIndex / itemsPerView)
                ? 'bg-primary-600'
                : 'bg-gray-300'
                }`}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      )}

      {selectedPlatform && (
        <div className="mt-0 p-4 sm:p-6 border border-gray-200 rounded-xl bg-gray-50">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            Configure {selectedPlatform.name}
          </h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {selectedPlatform.fields.map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  placeholder={`Enter your ${field}`}
                  value={fieldValues[selectedPlatform.name]?.[field] || ''}
                  onChange={(e) => handleFieldChange(selectedPlatform.name, field, e.target.value)}
                  className="w-full px-3 sm:px-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-4 transition border-purple-200 focus:border-purple-400 ring-purple-100 text-sm sm:text-base text-black"
                  required
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {platforms.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No platforms available at the moment.
        </div>
      )}
    </div>
  );
};

export default Slider;